import React from 'react'
import ReactDOM from 'react-dom'
import { FEAASElement } from '../components/FEAASElement.js'
import { ReactJSX, ReactModules } from '../components/FEAASReactElement.js'
import { kebabCase, reverseKebabCase } from '../utils/string.js'
import { Component, FEAASComponentProps } from './react.js'
import type { ExternalComponent, ExternalComponentOptions, JSONSchema, UISchema } from '@sitecore-feaas/sdk'

/** Checks if title exists in a property and if not, it generates it from (kebab-cased) property key */
const addTitles = (properties: { [key: string]: any }) =>
  Object.keys(properties).reduce((acc: { [key: string]: any }, key) => {
    if (properties[key].title) {
      acc[key] = properties[key]
    } else {
      const title = key
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
      acc[key] = { ...properties[key], title }
    }
    return acc
  }, {})

/** Converts the keys of an object to kebab case. */
const objectKeysToKebabCase = (object: { [key: string]: any } = {}) =>
  Object.keys(object).reduce((acc: { [key: string]: any }, key) => {
    acc[kebabCase(key)] = object[key]
    return acc
  }, {})

const transformSchema = (schema: JSONSchema) => {
  if (!schema) return
  if (!schema.properties) return schema
  let properties, required

  /* kebab-case properties */
  properties = objectKeysToKebabCase(schema.properties)
  required = (schema.required || []).map((item: string) => kebabCase(item))

  /* generate titles if not exist */
  properties = addTitles(properties)

  /* remove title from schema - it will be passed separately to use custom styling in the form */
  const title: string = undefined
  return { ...schema, properties, required, title }
}

const transformUiSchema = (uiSchema: UISchema, properties: { [key: string]: any }) => {
  if (!uiSchema || !properties) return
  let transformed: UISchema

  /* kebab-case keys */
  transformed = objectKeysToKebabCase(uiSchema)

  /* use updown input widget as default for numbers */
  const numberProperties = Object.keys(properties).filter((key) => /(integer|number)/.test(properties[key].type))
  numberProperties.forEach((propertyName) => {
    if (!transformed[propertyName]) transformed[propertyName] = { 'ui:widget': 'updown' }
    else if (!transformed[propertyName]['ui:widget']) transformed[propertyName]['ui:widget'] = 'updown'
  })

  return transformed
}
/** Parses a property value based on its property name and type defined in the JSON properties object. */
export const parseProps = (propName: string, propValue: string, properties: JSONSchema['properties']) => {
  //@ts-ignore
  switch (properties?.[propName]?.type) {
    case 'string':
      return propValue
    case 'object':
      return JSON.parse(propValue)
    case 'array':
      return JSON.parse(propValue)
    case 'number':
      return +propValue
    case 'integer':
      return +propValue
    case 'boolean':
      return propValue === 'true'
    default:
      return propValue
  }
}

export function FEAASExternalProps(element: FEAASElement): any {
  const id = element.getAttribute('data-external-id')
  const schema: JSONSchema = FEAASExternal.registered[id]?.schema
  const props = Array.from(element.attributes).reduce((prev, { name, value }) => {
    if (!name.startsWith('data-attribute') && !['class', 'id', 'contenteditable'].includes(name)) {
      return { ...prev, [reverseKebabCase(name)]: parseProps(reverseKebabCase(name), value, schema?.properties) }
    } else {
      return prev
    }
  }, {})
  return props
}

export type RegisteredComponents = {
  [id: string]: ExternalComponent
}
export type FEAASExternalProps = ReturnType<typeof FEAASExternalProps> & { dataExternalId: string }

export default class FEAASExternal extends FEAASElement<FEAASExternalProps, ReactModules> {
  static registered: RegisteredComponents = {}
  static registrationCallback: NodeJS.Timeout
  load() {
    return Promise.resolve({ React, ReactDOM })
  }

  update() {
    const { dataExternalId: id } = this.props
    const Component = FEAASExternal.registered[id]?.component
    if (!Component && this.childNodes.length > 0) return
    return super.update()
  }

  render(props: FEAASExternalProps, { React }: ReactModules): ReactJSX {
    const { dataExternalId: id } = props
    const Component = FEAASExternal.registered[id]?.component
    if (!Component) {
      console.warn('FEAASExternal: The component you requested is not registered')
    }
    return <>{Component && <Component {...props} />}</>
  }

  getProps() {
    return FEAASExternalProps(this)
  }

  /**
   * Register React component to be renderable as Sitecore component (in Components and Pages). Properties are defined
   * as {@link https://json-schema.org JSON Schema}, from which a configuration form will be produced using
   * {@link https://rjsf-team.github.io/react-jsonschema-form/ RJSF library}. The library allows passing a
   * {@link https://rjsf-team.github.io/react-jsonschema-form/docs/api-reference/uiSchema UI Schema} that allows tight
   * customization of form widgets, styles and behavior.
   *
   * @param {React.ComponentType}      component                     React component that will be rendered.
   * @param {string}                   options.title                 Component name as presented to the user.
   * @param {JSONSchema['properties']} options.properties            Object of properties.
   * @param {JSONSchema['type']}       options.properties[].type     JSON Schema type of a property.
   * @param {string}                   [options.properties[].title]  Optional title displayed to the user.
   * @param {string}                   [options.group]               Optional Image to be displayed in the UI.
   * @param {string}                   [options.thumbnail]           Optional title displayed to the user.
   * @param {UISchema}                 [options.ui]                  Optional component groupping.
   * @see https://rjsf-team.github.io/react-jsonschema-form/docs/api-reference/uiSchema
   * @see https://json-schema.org
   * @example
   *
   *   FEAAS.External.registerComponent(MyComponent, {
   *     // Name of a component as it will be presented to the user
   *     name: 'My Component',
   *     // Title of a component as it will be presented in the inputs form
   *     title: 'My Component',
   *     // Optional: Thumbnail image for component to be displayed in UI
   *     thumbnail: 'http://examaple.com/component.jpg',
   *     // Optional: Group components by name
   *     group: 'Assorted components',
   *     // Definitions of three configurable properties that MyComponent accepts
   *     properties: {
   *       // Color is a string, but only Red/Green/Blue are allowed
   *       color: { type: 'string', enum: ['red', 'green', 'blue'] },
   *       // Text is a property labelled as "Text summary"
   *       text: { type: 'string', title: 'Text summary' },
   *       // Count is a number with 1 as default value
   *       count: { type: 'number', default: 1 }
   *     },
   *     // Optional UI schema customization
   *     ui: {
   *       // Present color choices as radio buttons
   *       color: { 'ui:widget': 'radio' },
   *       // Text is a textarea with 5 rows
   *       text: { 'ui:widget': 'textarea', 'ui:options': { rows: 5 } },
   *       // Count is a number field with spinner button
   *       count: { 'ui:widget': 'updown' }
   *     },
   *     // Color and text area required properties
   *     required: ['color', 'text']
   *   })
   *
   */
  static registerComponent(component: React.ComponentType, options: ExternalComponentOptions) {
    if (!options.name)
      throw new Error('Could not register external component. Please make sure you provide a name in the options')
    const id = options.name
    const { thumbnail, group, ui: uiSchema, ...schemaOptions } = options
    const schema = { ...schemaOptions, type: 'object' } as JSONSchema
    FEAASExternal.registered[id] = {
      component,
      name: id,
      schema,
      uiSchema,
      thumbnail,
      group,
      title: schema?.title || id
    }
    FEAASExternal.setRegistrationCallback()
  }

  static setRegistrationCallback() {
    clearTimeout(FEAASExternal.registrationCallback)
    FEAASExternal.registrationCallback = setTimeout(() => {
      if (typeof window !== 'undefined') {
        // create components array from object
        const components = Object.keys(FEAASExternal.registered).map((id) => ({
          ...FEAASExternal.registered[id],
          name: id,
          schema: transformSchema(FEAASExternal.registered[id].schema),
          uiSchema: transformUiSchema(
            FEAASExternal.registered[id].uiSchema,
            FEAASExternal.registered[id].schema?.properties
          )
          //component: FEAASExternal.registered[name].component.toString()
        }))

        // send components from iframe to parent window
        window.parent?.postMessage(
          JSON.stringify({
            action: 'register-components',
            data: components
          }),
          '*'
        )
      }
    }, 30)
  }

  /*static loadComponents() {
    const components = JSON.parse(sessionStorage.getItem('feaas-external-components'))
    components.forEach((c: { name: string; schema: any; component: React.ComponentType }) => {
      const name = c.name
      const schema = c.schema
      const component = eval('(' + c.component + ')')
      FEAASExternal.registerComponent(component, schema, name)
    })
  }*/

  static Preview(props: Partial<FEAASComponentProps<any>> & { src: string }) {
    const src =
      props?.src || (typeof window !== 'undefined' ? window.location.search.match(/feaasSrc=([^&]+)/)?.[1] : null)
    return src ? <Component src={src} {...props} /> : null
  }

  static linkComponents(components: Promise<any>[]): () => any {
    return () => components.map((components) => null)
  }

  static getComponent(id: string) {
    return FEAASExternal.registered[id]
  }
}

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'feaas-external': {
        'data-external-id': string
        children?: any
        dangerouslySetInnerHTML?: { __html: string }
        [key: string]: any
      }
    }
  }
}

FEAASExternal.register('feaas-external')
FEAASExternal.setRegistrationCallback()

export const { getComponent, linkComponents, registerComponent } = FEAASExternal
export { FEAASExternal as External }
